import { useJobTime, entryMinutes, fmtHours } from '@/hooks/use-time'
import { loadGoogleMaps } from '@/lib/google-maps'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Trash2, Receipt, Check, FileText, Paperclip, MessageSquare } from 'lucide-react'
import { Modal, Field, Input } from '@/components/rm/form'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { signedUrl } from '@/lib/storage'
import { useJobs } from '@/hooks/use-jobs'
import { useInventory, useTaskMaterials } from '@/hooks/use-inventory'
import { useInvoices } from '@/hooks/use-invoices'
import { useClients, clientName } from '@/hooks/use-clients'
import { useTeam } from '@/hooks/use-company'
import { useAuth } from '@/hooks/use-auth'
import { Page, Card, DetailSkeleton, btnPrimary, btnSecondary } from '@/components/rm'
import { JobsWorkspacePage } from './jobs'
import { ThreadStrip } from '@/components/thread-strip'
import { ActivityTimeline } from '@/components/activity-timeline'
import { useJobActivity } from '@/hooks/use-activity'
import { JobProfitCard, CompletionCapture } from '@/components/job-profit-card'
import { JobAssetsCard } from '@/components/client-assets-card'
import { SafetyStrip } from '@/components/swms/safety-strip'
import { checkSafetyGate, type GateVerdict } from '@/lib/swms-gate'
import { useSwms, useJobHrcw, recordSafetyOverride } from '@/hooks/use-swms'
import { cn } from '@/lib/utils'
import { z } from 'zod'
import { phFormat, peso, manilaParts } from '@/lib/ph'

type Att = { kind: string; file_name: string | null; mime_type: string | null; storage_path: string | null; source_url: string | null }

// Renderable in an <img>? Browsers can't show HEIC, so those fall back to a chip.
function isRenderableImage(a: Att): boolean {
  const mime = a.mime_type ?? ''
  const name = a.file_name ?? ''
  if (/heic|heif/i.test(mime) || /\.hei[cf]$/i.test(name)) return false
  if (/^image\/(png|jpe?g|gif|webp|bmp|svg\+xml|avif)$/i.test(mime)) return true
  if (/\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(name)) return true
  // signature/camera/image fields are images even when mime/name are missing
  return a.kind === 'image' || a.kind === 'camera' || a.kind === 'signature'
}

function Attachment({ a }: { a: Att }) {
  const isImg = isRenderableImage(a)
  const isSig = a.kind === 'signature'
  const [broke, setBroke] = useState(false)
  const url = useQuery({
    queryKey: ['att-url', a.storage_path],
    enabled: !!a.storage_path,
    queryFn: async () => (a.storage_path ? signedUrl(a.storage_path, 3600) : null),
  })
  // Only a presigned URL can be an <img src>. `source_url` is the legacy RouteMate S3 location,
  // which is private: using it as a src renders a broken thumbnail with the filename as alt text,
  // which is indistinguishable from a missing file and hides the real cause (the presign was
  // refused). Keep it as a link target only.
  const imgSrc = url.data ?? undefined
  const href = url.data ?? a.source_url ?? undefined
  if (isImg && imgSrc && !broke) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="block" title={a.file_name ?? ''}>
        <img
          src={imgSrc}
          alt={a.file_name ?? ''}
          loading="lazy"
          onError={() => setBroke(true)}
          className={cn('max-h-44 w-auto max-w-[220px] rounded-[10px] border border-token-subtle object-contain transition hover:border-token-strong', isSig && 'bg-token-surface p-1')}
        />
      </a>
    )
  }
  const failed = isImg && (broke || (!url.isLoading && !url.data))
  return (
    <span className="inline-flex flex-col gap-1">
      <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-[8px] border border-token-subtle bg-token-surface px-2.5 py-1.5 text-[12.5px] font-semibold text-token-secondary hover:border-token-strong">
        {isImg ? <Paperclip className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
        <span className="max-w-[160px] truncate">{a.file_name || a.kind}</span>
      </a>
      {failed && <span className="text-[11.5px] text-token-warning">Couldn't load this image</span>}
    </span>
  )
}

export const Route = createFileRoute('/_app/jobs_/$id')({
  validateSearch: z.object({ view: z.enum(['record']).optional() }),
  component: JobDetailPage,
})

const money = (n: number) => peso(n, { whole: true })
const STAGES = ['Quoted', 'Scheduled', 'In progress', 'Invoiced', 'Paid'] as const
// 'paragraph' is NOT display-only in this product: it is the multi-line input, and Job
// Description — the most-read field a customer has — is one. Treating it as decoration hid every
// description on the job view, which is what sent the owner into the form editor and started
// the 2026-08-10 data-loss incident. Header and button stay decoration.
const DISPLAY_ONLY = new Set(['header', 'button'])
const MEDIA = new Set(['image', 'camera', 'document', 'signature'])

function stageIndex(status: string | null): number {
  const s = (status ?? '').toLowerCase()
  if (s.includes('paid')) return 4
  if (s.includes('invoiced')) return 3
  if (s.includes('progress')) return 2
  if (s.includes('done') || s.includes('complete')) return 2
  if (s.includes('schedul')) return 1
  return 0
}

// dd-MM-yyyy, hh:mm a — matches the Flutter job/task form.
function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  // Manila time (spec 001), not the device's.
  const p = (n: number) => String(n).padStart(2, '0')
  const m = manilaParts(d)
  let h = m.hour; const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12
  return `${p(m.day)}-${p(m.month)}-${m.year}, ${p(h)}:${p(m.minute)} ${ap}`
}

function fmtCustom(v: unknown, type: string): string {
  if (MEDIA.has(type)) {
    if (Array.isArray(v)) return v.length ? `📎 ${v.length} file${v.length === 1 ? '' : 's'}` : '—'
    if (v && typeof v === 'object') return '📎 Attachment'
    return '—'
  }
  if (v == null || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (type === 'date') return fmtDate(typeof v === 'string' ? v : String(v))
  if (Array.isArray(v)) return v.map((x) => fmtCustom(x, 'text')).filter((s) => s !== '—').join(', ') || '—'
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    if (o.value != null) return String(o.value)
    if (typeof o.address === 'string') return o.address
    if (typeof o.formatted === 'string') return o.formatted
    if (typeof o.name === 'string') return o.name
    if (typeof o.latitude === 'number') return `${o.latitude}, ${o.longitude}`
    return '—'
  }
  return String(v)
}

function JobDetailPage() {
  const { id } = Route.useParams()
  const { view } = Route.useSearch()
  const navigate = useNavigate()
  const { job, isLoadingDetail, update, remove } = useJobs(id)
  const { createFromJob } = useInvoices()
  const { clients } = useClients()
  const { members } = useTeam()
  // ETA-message state — must be declared before any early return (Rules of Hooks).
  const [etaOpen, setEtaOpen] = useState(false)
  const [captureOpen, setCaptureOpen] = useState(false)
  const [etaMins, setEtaMins] = useState(20)
  const [etaBusy, setEtaBusy] = useState(false)
  const [trackUrl, setTrackUrl] = useState<string | null>(null)
  // Spec 035. Both hooks are cheap and no-op for a company with no HRCW categories, but they must
  // still be declared here — above every early return — or the Rules of Hooks bite on job-not-found.
  const [gate, setGate] = useState<{ verdict: GateVerdict; to: string } | null>(null)
  const { user, companyId } = useAuth()
  const hrcw = useJobHrcw(job?.hrcw_categories)
  const { current: currentSwms } = useSwms(hrcw.isHighRisk ? Number(id) : undefined)

  // Reopening the job must resume the live share, or the operator loses the only way to stop it —
  // the same failure the iOS side had. RLS scopes this to the caller's company.
  const liveShare = useQuery({
    queryKey: ['job-tracking', id],
    queryFn: async () => {
      const { data } = await supabase.from('job_tracking_session')
        .select('token').eq('task_id', Number(id)).is('ended_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('started_at', { ascending: false }).limit(1).maybeSingle()
      return (data as { token: string } | null)?.token ?? null
    },
  })
  useEffect(() => { if (liveShare.data) setTrackUrl(`${window.location.origin}/t/${liveShare.data}`) }, [liveShare.data])
  const contactName = (cid: string) => { const c = clients.find((x) => x.id === cid); return c ? clientName(c) : cid }
  const userName = (uid: string) => { const u = members.find((x) => x.id === uid); return u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || uid : uid }

  const extra = useQuery({
    queryKey: ['job-extra', id],
    queryFn: async () => {
      const task = await supabase
        .from('task')
        .select('user_id, contact_id, custom_form_id, name, status, address, line1, line2, city, state, postal_code, manual_address, planned_start_date, planned_end_date, hrcw_categories')
        .eq('id', id).maybeSingle()
      const t = task.data as null | {
        user_id: string | null; contact_id: string | null; custom_form_id: string | null
        name: string | null; status: string | null; address: string | null; line1: string | null; line2: string | null
        city: string | null; state: string | null; postal_code: string | null; manual_address: boolean | null
        planned_start_date: string | null; planned_end_date: string | null
        hrcw_categories: string[] | null
      }
      const [contact, user, quote, invoice, fields, values, attachments] = await Promise.all([
        t?.contact_id ? supabase.from('contact').select('first_name, last_name, organization, email, phone, mobile').eq('id', t.contact_id).maybeSingle() : Promise.resolve({ data: null }),
        t?.user_id ? supabase.from('users').select('first_name, last_name, email').eq('id', t.user_id).maybeSingle() : Promise.resolve({ data: null }),
        supabase.from('quote').select('id, quote_number, total').eq('task_id', id).is('deleted_at', null).limit(1).maybeSingle(),
        supabase.from('customer_invoice').select('id, total').eq('task_id', id).is('deleted_at', null).limit(1).maybeSingle(),
        t?.custom_form_id ? supabase.from('custom_form_field').select('id, name, type, row, hidden').eq('custom_form_id', t.custom_form_id).is('deleted_at', null).order('row', { ascending: true }) : Promise.resolve({ data: [] }),
        supabase.from('task_field_value').select('field_id, value').eq('task_id', id),
        supabase.from('task_attachment').select('field_id, kind, file_name, mime_type, storage_path, source_url').eq('task_id', id),
      ])
      let items: Array<{ description: string; amount: number }> = []
      const qid = (quote.data as { id: number } | null)?.id
      if (qid) {
        const li = await supabase.from('quote_item').select('description, amount').eq('quote_id', qid).order('position')
        items = (li.data ?? []) as Array<{ description: string; amount: number }>
      }
      const valueMap: Record<string, unknown> = {}
      ;(values.data ?? []).forEach((v) => { const r = v as { field_id: string; value: unknown }; valueMap[r.field_id] = r.value })
      const attachMap: Record<string, Array<{ kind: string; file_name: string | null; mime_type: string | null; storage_path: string | null; source_url: string | null }>> = {}
      ;(attachments.data ?? []).forEach((a) => { const r = a as { field_id: string; kind: string; file_name: string | null; mime_type: string | null; storage_path: string | null; source_url: string | null }; (attachMap[r.field_id] ||= []).push(r) })
      return {
        t,
        contact: contact.data as null | { first_name: string | null; last_name: string | null; organization: string | null; email: string | null; phone: string | null; mobile: string | null },
        user: user.data as null | { first_name: string | null; last_name: string | null; email: string | null },
        quote: quote.data as { quote_number: string; total: number } | null,
        invoice: invoice.data as { total: number } | null,
        items,
        fieldList: (fields.data ?? []) as Array<{ id: string; name: string; type: string; row: number | null; hidden: boolean | null }>,
        valueMap,
        attachMap,
        // Photos that belong to the JOB rather than to a custom field. Photos taken in the app are
        // 'proof'; the ~600 migrated from RouteMate Plus landed as 'image' with no field_id.
        // Reading only 'proof' hid every migrated photo on the web while the phones showed them,
        // because the phones widened this read months ago and the web never did (a customer,
        // 2026-08-12: "images are not showing on the desktop, only in the app").
        // field_id must be null: an image that DOES belong to a field is already rendered by that
        // field's row, and matching on kind alone would show it twice.
        proof: ((attachments.data ?? []) as Array<{ field_id: string | null; kind: string; file_name: string | null; mime_type: string | null; storage_path: string | null; source_url: string | null }>)
          .filter((a) => (a.kind === 'proof' || a.kind === 'image') && !a.field_id),
      }
    },
    enabled: !!id,
  })

  // The wide workspace must be rendered in the SAME tree position in every branch below, or
  // React unmounts it. Selecting a job in the rail changes :id, which puts this component back
  // into its loading state; when the early returns rendered only a skeleton, that skeleton
  // replaced the whole two-column workspace — the job list included — and the workspace was then
  // rebuilt from scratch. That is the flicker on every click. The rail owns its own data, so it
  // has no reason to wait for the detail query.
  const wideWorkspace = view !== 'record' ? (
    <div className="hidden min-[1200px]:block">
      <JobsWorkspacePage selectedJobId={Number(id)} />
    </div>
  ) : null

  if (isLoadingDetail) {
    return (
      <>
        {wideWorkspace}
        <div className={view === 'record' ? 'block' : 'min-[1200px]:hidden'}>
          <Page><DetailSkeleton /></Page>
        </div>
      </>
    )
  }
  if (!job) {
    return (
      <>
        {wideWorkspace}
        <div className={view === 'record' ? 'block' : 'min-[1200px]:hidden'}>
          <Page>
            <h1 className="font-display text-[22px] font-bold text-token-primary">Job not found</h1>
            <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/jobs' })}>Back to jobs</button>
          </Page>
        </div>
      </>
    )
  }

  const status = job.status || 'Pending'
  const cur = stageIndex(status)
  const isCompleted = status.toLowerCase().includes('complete')
  const x = extra.data
  const t = x?.t
  const cName = x?.contact ? ([x.contact.first_name, x.contact.last_name].filter(Boolean).join(' ').trim() || x.contact.organization || 'Client') : ''
  const uName = x?.user ? ([x.user.first_name, x.user.last_name].filter(Boolean).join(' ').trim() || x.user.email || '') : ''
  // Branching on `manual_address` decided WHERE to look rather than what exists: RouteMate-era
  // jobs keep the street in line1 with `address` empty and the flag false, so the non-manual
  // branch returned nothing and the job showed no address at all.
  const address = (() => {
    const street = [t?.line1, t?.line2].filter(Boolean).join(' ').trim()
    const region = [t?.city, t?.state, t?.postal_code].filter(Boolean).join(' ').trim()
    if (street) return [street, region].filter(Boolean).join(', ')
    return (t?.address ?? job.address ?? '').trim() || region
  })()
  const jobTotal = x?.invoice?.total ?? x?.quote?.total ?? 0

  // Unified field list — system fields (native columns) then custom fields, exactly like the Flutter auto job/task form.
  // Custom values now come from the normalized task_field_value / task_attachment tables.
  type Row = { label: string; value: string; attachments?: Att[] }
  const valueMap = x?.valueMap ?? {}
  const attachMap = x?.attachMap ?? {}
  const systemFields: Row[] = [
    { label: 'Assigned User', value: uName || '—' },
    { label: 'Job Name', value: job.name || '—' },
    { label: 'Address', value: address || '—' },
    { label: 'Start Date', value: fmtDate(t?.planned_start_date ?? null) },
    { label: 'End Date', value: fmtDate(t?.planned_end_date ?? null) },
    { label: 'Status', value: status },
    { label: 'Contact', value: cName || '—' },
  ]
  const idOf = (v: unknown): string => (typeof v === 'string' ? v : (v && typeof v === 'object' && 'id' in (v as object) ? String((v as { id: unknown }).id) : ''))
  const customFields: Row[] = (x?.fieldList ?? [])
    .filter((f) => !f.hidden && !DISPLAY_ONLY.has(f.type))
    // A paragraph with no answer is most likely a static text block on someone's form; showing
    // an em-dash row for it would be clutter. A paragraph WITH an answer is an input, and shows.
    .filter((f) => f.type !== 'paragraph' || valueMap[f.id] != null)
    .map((f) => {
      const atts = attachMap[f.id]
      if (MEDIA.has(f.type)) return { label: f.name, value: atts?.length ? '' : '—', attachments: atts }
      const raw = valueMap[f.id]
      if (f.type === 'contact') { const cid = idOf(raw); return { label: f.name, value: cid ? contactName(cid) : '—' } }
      if (f.type === 'user') { const uid = idOf(raw); return { label: f.name, value: uid ? userName(uid) : '—' } }
      return { label: f.name, value: fmtCustom(raw, f.type) }
    })
  const allFields = [...systemFields, ...customFields]

  // Spec 035 FR-002 — the safety gate. Client-side by design: it decides whether to show a modal,
  // it adds no work to the write path, and for a company with no HRCW categories it is one
  // array-length check that runs before any query. See src/lib/swms-gate.ts.
  const setStatus = async (s: string, opts?: { overrideReason?: string }) => {
    const verdict = checkSafetyGate({
      fromStatus: status, toStatus: s, categories: hrcw.categories,
      swms: currentSwms ? { status: currentSwms.status, signed_at: currentSwms.signed_at } : null,
      enforcement: hrcw.enforcement,
    })
    if (!verdict.allow && !opts?.overrideReason) { setGate({ verdict, to: s }); return }

    try {
      await update.mutateAsync({ id: job.id, status: s })
      if (opts?.overrideReason) {
        // Recorded before the success toast, and NOT swallowed: an override nobody recorded is
        // the one failure in this feature that would leave an owner blind to it.
        await recordSafetyOverride({
          companyId: companyId!, taskId: job.id, from: status, to: s,
          reason: opts.overrideReason, userId: user?.id ?? null,
        })
      }
      toast.success(`Marked ${s}`)
      // Capture prompt (spec 032): once, on a genuine completion, only when nothing is logged.
      // Asking someone who already ran the timer would punish the person doing it right.
      if (['Completed', 'Completed-Follow-Up', 'Closed'].includes(s)) {
        const { count } = await supabase.from('time_entry')
          .select('id', { count: 'exact', head: true }).eq('task_id', job.id)
        if ((count ?? 0) === 0) setCaptureOpen(true)
      }
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const completeAndInvoice = async () => {
    try {
      if (stageIndex(status) < 2) await update.mutateAsync({ id: job.id, status: 'Completed' })
      const { invoiceId } = await createFromJob.mutateAsync({ jobId: job.id })
      toast.success('Invoice drafted from job')
      navigate({ to: '/invoices/$id', params: { id: String(invoiceId) } })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const del = async () => {
    if (!confirm('Delete this job? Any reminders still queued for it are cancelled. Quotes and invoices already raised against it are kept.')) return
    try {
      await remove.mutateAsync(job.id)
      // Deleting the thing must also throw away the half-finished version of it — the mail-app
      // rule. Without this, opening the editor again would restore a draft of a job that no
      // longer exists and quietly offer to recreate it.
      try { localStorage.removeItem(`sp.jobdraft.${companyId ?? 'x'}.${job.id}`) } catch { /* ignore */ }
      toast.success('Job deleted')
      navigate({ to: '/jobs' })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not delete the job') }
  }

  const custMobile = x?.contact?.mobile || x?.contact?.phone || ''
  // Spec 016: the customer now gets a live tracking link, not just a sentence. The server owns
  // the session, the SMS and the wording — this only supplies the stated ETA.
  const etaPreview = `Hi ${x?.contact?.first_name ?? 'there'}, I'm on my way${address ? ` to ${address}` : ''}. ETA ${phFormat(new Date(Date.now() + etaMins * 60000), 'time', { hour: 'numeric', minute: '2-digit' })}. Track me here: …`
  const sendEta = async () => {
    setEtaBusy(true)
    const { data, error } = await supabase.functions.invoke('tracking-start', {
      body: { taskId: job.id, etaMinutes: etaMins, origin: window.location.origin },
    })
    setEtaBusy(false)
    const res = data as { ok?: boolean; url?: string; resumed?: boolean } | null
    if (error || !res?.ok) {
      const ctx = (error as { context?: Response } | null)?.context
      let msg = 'Could not send'
      let url: string | undefined
      // A 402/502 still means the session was created and the link works — only the text failed.
      // Hand the operator the link rather than telling them nothing happened.
      try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error; url = j?.url } } catch { /* */ }
      if (url) { setTrackUrl(url); setEtaOpen(false) }
      return toast.error(msg)
    }
    setTrackUrl(res.url ?? null)
    toast.success(res.resumed ? 'Already sharing — link re-sent' : 'Tracking link sent')
    setEtaOpen(false)
  }

  const stopTracking = async () => {
    const { error } = await supabase.functions.invoke('tracking-end', { body: { taskId: job.id } })
    if (error) return toast.error('Could not stop sharing')
    setTrackUrl(null)
    toast.success('Location sharing stopped')
  }

  return (
    <>
      {wideWorkspace}
      <div className={view === 'record' ? 'block' : 'min-[1200px]:hidden'}>
      <Page>
      {/* Header */}
      <div className="mb-[22px] flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
        <div>
          <button onClick={() => view === 'record' ? navigate({ to: '/jobs/$id', params: { id } }) : navigate({ to: '/jobs' })} className="text-[12.5px] font-semibold text-token-muted hover:text-token-secondary">{view === 'record' ? 'Back to Jobs workspace' : `Jobs / #J-${job.id}`}</button>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[25px] font-bold tracking-[-0.02em] text-token-primary">{job.name || 'Untitled job'}</h1>
            <span className={cn('rounded-full px-3 py-1 text-[12px] font-bold', cur >= 2 ? 'bg-token-success-surface text-token-success' : 'bg-token-info-surface text-token-info')}>{status}</span>
          </div>
          <p className="mt-1.5 text-[13.5px] text-token-secondary">{cName || 'No client'}{address ? ` · ${address}` : ''}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2.5">
          {custMobile && !trackUrl && <button className={btnSecondary} onClick={() => setEtaOpen(true)}><MessageSquare className="h-4 w-4" /> On my way</button>}
          {trackUrl && <button className={btnSecondary} onClick={stopTracking}>Stop sharing</button>}
          <button className={btnSecondary} onClick={() => navigate({ to: '/jobs/$id/edit', params: { id: String(job.id) } })}>Edit</button>
          {stageIndex(status) < 2 && <button className={btnSecondary} onClick={() => setStatus('Completed')}>Complete</button>}
          <button className={btnPrimary} onClick={completeAndInvoice} disabled={createFromJob.isPending}>
            <Receipt className="h-4 w-4" /> {createFromJob.isPending ? 'Invoicing…' : 'Create invoice'}
          </button>
        </div>
      </div>

      {/* Safety (spec 035). Renders nothing at all unless this job carries HRCW categories, so a
          carpet cleaner's job page is byte-for-byte what it was before this feature existed. It
          sits ABOVE the stepper deliberately — the gate fires on the stepper, and a warning that
          appears below the control it governs is a warning nobody reads first. */}
      <SafetyStrip job={{ id: job.id, name: job.name, address, hrcw_categories: job.hrcw_categories ?? null, status, contactId: t?.contact_id ?? null }} contactName={cName} />

      {/* Linked thread */}
      <div className="mb-5"><ThreadStrip anchor={{ taskId: job.id }} current="job" /></div>

      {/* Status stepper */}
      <div className="mb-5 overflow-x-auto rounded-[14px] border border-token-subtle bg-token-surface">
      <div className="flex min-w-0 items-center px-2 py-5 sm:min-w-[560px] sm:px-6">
        {STAGES.map((label, i) => (
          <div key={label} className={cn('flex min-w-0 items-center', i === STAGES.length - 1 ? 'w-[64px] flex-none sm:w-[84px]' : 'flex-1')}>
            <div className="flex w-[64px] flex-none flex-col items-center gap-1.5 sm:w-[84px]">
              <span className={cn('flex h-[26px] w-[26px] items-center justify-center rounded-full text-[13px]', i < cur ? 'bg-token-success text-token-inverse' : i === cur ? 'bg-brand text-brand-fg' : 'bg-canvas-subtle text-token-muted')}>
                {i < cur ? <Check className="h-3.5 w-3.5" /> : i === cur ? '●' : '○'}
              </span>
              <span className={cn('text-center text-[11px] font-bold sm:text-[12.5px]', i < cur ? 'text-token-primary' : i === cur ? 'text-token-warning' : 'text-token-muted')}>{isCompleted && i === cur ? 'Completed' : label}</span>
            </div>
            {i < STAGES.length - 1 && <div className="mb-[22px] h-0.5 min-w-0 flex-1" style={{ background: i < cur ? 'var(--success)' : i === cur ? 'var(--brand)' : 'var(--border-subtle)' }} />}
          </div>
        ))}
      </div>
      </div>

      {/* The workspace already supplies a left job rail. Keep this detail surface single-column
          until a genuinely wide canvas is available; otherwise the former 340px utility rail
          would leave the actual job form too narrow to read. */}
      <div className="grid grid-cols-1 gap-5 min-[1600px]:grid-cols-[1fr_340px]">
        {/* Job/Task form — system + custom fields, Flutter order */}
        <div className="flex flex-col gap-5">
          <Card className="p-[22px]">
            <div className="mb-1">
              <p className="font-display text-[15px] font-bold text-token-primary">Job / Task</p>
              <p className="text-[12.5px] text-token-secondary">Auto created job/task form</p>
            </div>
            <div className="mt-3">
              {allFields.map((row, i) => (
                <div key={row.label + i} className="flex items-start justify-between gap-6 border-b border-token-subtle py-2.5 last:border-0">
                  <span className="flex-none text-[13.5px] text-token-secondary">{row.label}</span>
                  {row.attachments?.length ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      {row.attachments.map((a, j) => <Attachment key={j} a={a} />)}
                    </div>
                  ) : (
                    <span className="text-right text-[14px] font-medium text-token-primary">{row.value}</span>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Photos sit in the WIDE column, under the job form. They were the ninth card in a
              340px rail and the owner had to scroll past everything to find them (2026-08-12).
              Four across instead of two: the same thumbnails, twice the width to read them. */}
          {(x?.proof.length ?? 0) > 0 && (
            <Card className="p-[22px]">
              <div className="mb-3.5 flex items-center justify-between">
                <span className="font-display text-[15px] font-bold text-token-primary">Photos</span>
                <span className="text-[12.5px] text-token-secondary">{x!.proof.length} on this job</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                {x!.proof.map((a, i) => (
                  <Attachment key={a.storage_path ?? i} a={{ ...a, field_id: '' } as Att} />
                ))}
              </div>
            </Card>
          )}

          {(x?.items.length ?? 0) > 0 && (
            <Card className="p-[22px]">
              <div className="mb-3.5 flex items-center justify-between">
                <span className="font-display text-[15px] font-bold text-token-primary">Materials &amp; labour</span>
                <span className="text-[12.5px] text-token-secondary">Synced from quote {x!.quote?.quote_number}</span>
              </div>
              {x!.items.map((it, i) => (
                <div key={i} className="flex justify-between border-b border-token-subtle py-2.5 text-[13.5px] last:border-0">
                  <span className="text-token-primary">{it.description}</span><span className="font-bold tabular-nums text-token-primary">{money(Number(it.amount))}</span>
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Job total */}
        <div className="flex flex-col gap-5">
          {/* Order set by the owner (2026-08-12): the three things looked at while a job is live
              come first — where it is, how long it took, what the customer was told. Money and
              stock sit below them, and the destructive action stays last. */}
          {address && <LocationCard address={address} />}
          <TimeCard taskId={job.id} />
          <JobActivity taskId={job.id} />
          <JobProfitCard taskId={job.id} />
          <MaterialsCard taskId={job.id} />
          <JobAssetsCard taskId={job.id} contactId={t?.contact_id ?? null} />
          <Card className="p-5">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Job total</p>
            <p className="font-display text-[30px] font-bold tabular-nums text-token-primary">{money(jobTotal)}</p>
            <p className="mt-1 text-[12.5px] text-token-secondary">{x?.invoice ? 'from invoice' : x?.quote ? 'from accepted quote' : 'no quote linked'}</p>
          </Card>

          {/* Delete lives in its own card, not tucked under the job total. A customer could delete a job
              on the phone and not on the desktop (2026-08-12) — the action was here all along, as
              grey text below a large money figure, which is nobody's idea of where to look. */}
          <Card className="p-5">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Danger zone</p>
            <p className="mb-3 text-[12.5px] leading-relaxed text-token-secondary">Deleting cancels any reminders still queued for this job. Quotes and invoices already raised are kept.</p>
            <button onClick={del} className="inline-flex items-center gap-1.5 rounded-[10px] border border-token-danger px-3 py-2 text-[12.5px] font-semibold text-token-danger transition-colors hover:bg-token-danger-surface"><Trash2 className="h-3.5 w-3.5" /> Delete job</button>
          </Card>

        </div>
      </div>

      <Modal
        open={etaOpen}
        onClose={() => setEtaOpen(false)}
        title="Text “on my way”"
        footer={<>
          <button className={btnSecondary} onClick={() => setEtaOpen(false)}>Cancel</button>
          <button className={btnPrimary} onClick={sendEta} disabled={etaBusy}>{etaBusy ? 'Sending…' : 'Send tracking link'}</button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-[13px] text-token-secondary">Texts <b>{cName || 'the customer'}</b> at <b>{custMobile}</b> a live tracking link showing they’re on the way. You can stop sharing at any time.</p>
          <Field label="ETA (minutes)"><Input type="number" min={0} value={etaMins} onChange={(e) => setEtaMins(e.target.valueAsNumber || 0)} /></Field>
          <div className="rounded-[10px] border border-token-subtle bg-token-surface p-3 text-[13px] text-token-primary">{etaPreview}</div>
          <p className="text-[12px] text-token-secondary">Uses 1 SMS from your allowance/credits.</p>
        </div>
      </Modal>
      {/* Mounted only while open: the modal's hours field initialises from useState, and mounting
          it at page load captured suggestedHours before the task row had arrived — every prompt
          said 1h regardless of schedule. Conditional mount makes initialisation happen at open
          time, when the duration is actually known. */}
      {captureOpen && <CompletionCapture
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        taskId={job.id}
        suggestedHours={(() => {
          const a = t?.planned_start_date ? new Date(t.planned_start_date).getTime() : null
          const b = t?.planned_end_date ? new Date(t.planned_end_date).getTime() : null
          if (a && b && b > a) return Math.min(24, Math.round(((b - a) / 3600000) * 2) / 2)
          return 1
        })()}
        source={t?.planned_start_date && t?.planned_end_date ? 'the scheduled duration' : 'a starting point'}
      />}

      {gate && !gate.verdict.allow && (
        <SafetyGateModal
          verdict={gate.verdict}
          onClose={() => setGate(null)}
          onOpenSwms={() => { setGate(null); navigate({ to: '/jobs/$id/swms', params: { id } }) }}
          onOverride={async (reason) => { const to = gate.to; setGate(null); await setStatus(to, { overrideReason: reason }) }}
        />
      )}
    </Page>
      </div>
    </>
  )
}

/**
 * The gate (FR-002). Warn mode lets the person through with a recorded reason; block mode does not.
 * Both leave a trail, and both offer the one-tap path to the SWMS first — the point is to make
 * doing it right faster than working around it, not to make the product an obstacle.
 */
function SafetyGateModal({
  verdict, onClose, onOpenSwms, onOverride,
}: {
  verdict: Extract<GateVerdict, { allow: false }>
  onClose: () => void
  onOpenSwms: () => void
  onOverride: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const blocking = verdict.mode === 'block'

  return (
    <Modal open title={verdict.title} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-[13px] leading-relaxed text-token-secondary">{verdict.body}</p>
        <button className={btnPrimary + ' w-full'} onClick={onOpenSwms}>Open the SWMS</button>
        <div className="border-t border-token-subtle pt-3">
          <p className="mb-2 text-[12.5px] font-medium text-token-primary">
            {blocking ? 'Start anyway (admin override)' : 'Start anyway'}
          </p>
          <Field label="Why are you starting without a signed SWMS?">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="This is recorded against the job." />
          </Field>
          <div className="mt-3 flex gap-2">
            <button
              className={btnSecondary}
              disabled={busy || !reason.trim()}
              onClick={async () => { setBusy(true); await onOverride(reason.trim()); setBusy(false) }}
            >
              {busy ? 'Starting…' : 'Start anyway'}
            </button>
            <button className={btnSecondary} onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function LocationCard({ address }: { address: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let cancelled = false
    loadGoogleMaps()
      .then((g) => {
        new g.maps.Geocoder().geocode({ address }, (results, status) => {
          if (cancelled || status !== 'OK' || !results?.[0] || !ref.current) { if (status !== 'OK') setFailed(true); return }
          const loc = results[0].geometry.location
          const map = new g.maps.Map(ref.current, {
            center: loc, zoom: 15, disableDefaultUI: true, gestureHandling: 'none',
            clickableIcons: false, keyboardShortcuts: false,
          })
          new g.maps.Marker({ position: loc, map })
        })
      })
      .catch(() => setFailed(true))
    return () => { cancelled = true }
  }, [address])
  if (failed) return null
  return (
    <Card className="overflow-hidden p-0">
      <a
        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
        target="_blank" rel="noreferrer" title="Open directions in Google Maps"
        className="block"
      >
        <div ref={ref} className="pointer-events-none h-[190px] w-full" />
        <p className="px-4 py-2.5 text-[12.5px] text-token-secondary">{address} · <span className="text-token-info">Directions ↗</span></p>
      </a>
    </Card>
  )
}

function TimeCard({ taskId }: { taskId: number }) {
  const { entries, running, clockOn, clockOff } = useJobTime(taskId)
  const { members } = useTeam()
  const { role } = useAuth()
  const name = (uid: string) => {
    const m = members.find((x) => x.id === uid)
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Tech' : 'Tech'
  }
  const list = entries.data ?? []
  const total = list.reduce((s, e) => s + entryMinutes(e), 0)
  const runningHere = running.data?.task_id === taskId
  return (
    <Card className="p-5">
      <div className="mb-2.5 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Time on job</p>
        <span className="font-display text-[15px] font-bold tabular-nums text-token-primary">{fmtHours(total)}</span>
      </div>
      {list.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {list.slice(0, 6).map((e) => (
            <div key={e.id} className="flex items-center justify-between text-[12.5px]">
              <span className="truncate text-token-secondary">{name(e.user_id)} · {phFormat(new Date(e.started_at), 'date', { day: 'numeric', month: 'short' })} {phFormat(new Date(e.started_at), 'time', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
              <span className={e.ended_at ? 'tabular-nums text-token-primary' : 'font-bold text-token-success'}>{e.ended_at ? fmtHours(entryMinutes(e)) : 'running'}</span>
            </div>
          ))}
        </div>
      )}
      {(() => {
        if (role !== 'company-admin') return null
        const cost = list.reduce((s, e) => {
          const r = Number(members.find((m) => m.id === e.user_id)?.hourly_cost ?? 0)
          return s + (entryMinutes(e) / 60) * r
        }, 0)
        if (cost <= 0) return null
        return (
          <div className="mb-3 flex items-center justify-between rounded-[8px] bg-token-selected px-2.5 py-1.5 text-[12px] font-semibold">
            <span className="text-token-success">Labour cost so far</span>
            <span className="tabular-nums text-token-success">{money(cost)}</span>
          </div>
        )
      })()}
      {runningHere ? (
        <button onClick={() => clockOff.mutate()} disabled={clockOff.isPending}
          className="w-full rounded-[10px] bg-token-danger py-2.5 text-[13px] font-bold text-token-inverse disabled:opacity-50">■ Clock off</button>
      ) : (
        <button onClick={() => clockOn.mutate()} disabled={clockOn.isPending}
          className="w-full rounded-[10px] bg-brand py-2.5 text-[13px] font-bold text-accent disabled:opacity-50">▶ Clock on</button>
      )}
      {running.data && !runningHere && <p className="mt-2 text-[11.5px] text-token-warning">You're clocked on to another job — starting here will clock that one off.</p>}
    </Card>
  )
}


function MaterialsCard({ taskId }: { taskId: number }) {
  const { items } = useInventory()
  const { materials, materialsCost, log, remove } = useTaskMaterials(taskId)
  const [itemId, setItemId] = useState('')
  const [qty, setQty] = useState(1)

  const add = async () => {
    if (!itemId || qty <= 0) return
    try { await log.mutateAsync({ itemId, qty }); setItemId(''); setQty(1) }
    catch (e) { toast.error((e as Error).message) }
  }

  return (
    <Card className="p-5">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Materials used</p>
      {materials.length === 0 ? (
        <p className="mb-3 text-[13px] text-token-secondary">No materials logged yet.</p>
      ) : (
        <div className="mb-3 flex flex-col gap-1.5">
          {materials.map((m) => (
            <div key={m.id} className="flex items-center justify-between text-[13px]">
              <span className="text-token-primary">{m.item?.name ?? 'Item'} <span className="text-token-secondary">· {m.qty} {m.item?.unit ?? ''}</span></span>
              <span className="flex items-center gap-2">
                <span className="tabular-nums text-token-secondary">{money(m.qty * m.unit_cost_at_time)}</span>
                <button aria-label="Remove material" onClick={() => remove.mutate(m.id)} className="text-token-muted transition-colors hover:text-token-danger"><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            </div>
          ))}
          <div className="mt-1 flex items-center justify-between border-t border-token-subtle pt-2 text-[12px] font-semibold">
            <span className="text-token-success">Materials cost</span>
            <span className="tabular-nums text-token-success">{money(materialsCost)}</span>
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-[12px] text-token-secondary">Add stock in <a href="/inventory" className="font-semibold text-token-primary underline">Inventory</a> first.</p>
      ) : (
        <div className="flex items-center gap-2">
          <select value={itemId} onChange={(e) => setItemId(e.target.value)} className="min-w-0 flex-1 rounded-[9px] border border-token-subtle bg-token-surface px-2.5 py-2 text-[13px] text-token-primary">
            <option value="">Add a material…</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.qty_on_hand} {i.unit})</option>)}
          </select>
          <input type="number" min="0" step="1" value={qty} onChange={(e) => setQty(Number(e.target.value))} className="w-16 rounded-[9px] border border-token-subtle bg-token-surface px-2 py-2 text-[13px] tabular-nums" />
          <button onClick={add} disabled={!itemId || log.isPending} className="rounded-[9px] bg-brand px-3 py-2 text-[13px] font-bold text-accent disabled:opacity-50">Add</button>
        </div>
      )}
      <p className="mt-2 text-[11.5px] text-token-secondary">Deducts from stock and feeds job profit. Not shown on the customer invoice.</p>
    </Card>
  )
}

/** 011 T005. Replaces EmailsCard: same queue and send history, plus the job's own stamps. */
function JobActivity({ taskId }: { taskId: number }) {
  const { data = [], isLoading } = useJobActivity(taskId)
  return <ActivityTimeline entries={data} isLoading={isLoading} title="Activity" />
}
